import { IntervalTimer } from "../../../base/common/async.js";
import { Disposable } from "../../../base/common/lifecycle.js";
import { IHierarchyNode, IHierarchyService, NodeFlags } from "../../hierarchy/common/hierarchy.js";
import { HierarchyNode } from "../../hierarchy/common/hierarchyService.js";
import { ILogService } from "../../log/common/log.js";
import { registerAnimationClip } from "../../resources/common/registerLoadedResources.js";
import { resourceUsageUuid, IResourceService, type ResourceUsage } from "../../resources/common/resources.js";
import { getMixerClips, getMixerRoot } from "./helper.animation.js";
import { isGeometry, isMaterial, isObject3D, isScene, isTexture } from "./helper.js";
import { textureTypeName } from "./helper.textures.js";
import { isNodeMaterial, type NodeLike, type NodeMaterialLike } from "./helper.nodes.js";
import { getChildNodes, getMaterialNodeRoots } from "./nodegraph.tsl.js";

/**
 * Populates {@link IResourceService} with the materials, textures and geometries of the
 * inspected three.js scene(s).
 *
 * Registration is always WEAK (see resources.ts) — this tracker must never retain
 * host-page objects; the chrome-extension build injects the editor into third-party pages.
 *
 * Sources:
 * - `IHierarchyService.onNodeAdded` covers initial tree creation (fired recursively per
 *   node) and runtime additions (the three tree provider already normalizes `childadded`
 *   events + child polling into hierarchy events).
 * - A periodic sweep catches what hierarchy events can't: `mesh.material` reassignment,
 *   texture-slot changes, scene.environment/background swaps. Re-registration of known
 *   resources is a cheap dedup no-op (and refreshes name snapshots).
 *
 * Deliberately NO unregistration on `onNodeRemoved`: removing a node does not mean its
 * (possibly shared) materials/textures died — garbage collection is the sole removal
 * path, observed by the resource service's FinalizationRegistry.
 *
 * Textures inside TSL node graphs (node materials) are discovered by walking the material's
 * node tree — the same traversal the node-graph view uses — so a `texture()` used only via
 * `colorNode`/`fragmentNode`/etc. is registered too, not just direct `isTexture` properties.
 *
 * Usage tracking ("used by") piggybacks on the same walks: every mesh→geometry,
 * mesh→material, material→texture and scene→environment/background edge is recorded into
 * the registry as a primitive consumer snapshot, and each completed full pass prunes edges
 * that were not re-seen (see IResourceService.completeUsagePass).
 */
export class ThreeResourceTracker extends Disposable {

    private static readonly SWEEP_INTERVAL = 2000;
    /** Max hierarchy nodes visited per sweep tick; huge scenes continue next tick. */
    private static readonly SWEEP_BUDGET = 5000;
    /** Safety cap on nodes visited per node-material graph walk (guards cycles / huge graphs). */
    private static readonly NODE_GRAPH_BUDGET = 5000;
    /** Max objects visited across ALL loaded-model subtree walks per sweep pass. */
    private static readonly MODEL_WALK_BUDGET = 20000;

    private _sweepIterator: Iterator<IHierarchyNode> | null = null;

    constructor(
        @IHierarchyService private readonly hierarchy: IHierarchyService,
        @IResourceService private readonly resources: IResourceService,
        @ILogService private readonly logService: ILogService,
    ) {
        super();

        this._register(hierarchy.onNodeAdded(node => this.trackNode(node)));

        // The scene may already be loaded when we start
        this.rescan();

        const timer = this._register(new IntervalTimer());
        timer.cancelAndSet(() => this.sweep(), ThreeResourceTracker.SWEEP_INTERVAL);
    }

    /**
     * Walk the whole hierarchy again.
     *
     * The constructor's walk is a snapshot: on a host page the editor can attach
     * after the scene was built and before the hierarchy carries it, and then
     * nothing is registered until some later node-added event — which read as
     * "0 B of VRAM" in the Performance panel. Anyone who finds the registry
     * empty can ask for another pass.
     */
    rescan(): void {
        for (const node of this.hierarchy.iterateNodes()) {
            this.trackNode(node);
        }
        // The iteration above is a full usage pass. trackNode defers per node via
        // queueMicrotask (queued during the loop), so queueing the model walk +
        // completion HERE runs them after all of them (FIFO).
        queueMicrotask(() => { this.trackModels(); this.trackMixers(); this.resources.completeUsagePass(); });
    }

    /**
     * Primitive consumer snapshot for usage tracking ("used by") — uuid + name
     * strings only, never the object itself (weak-registry rule).
     */
    private usageSnapshot(obj: object): { uuid: string; name: string; type: string; ref: WeakRef<object> } | null {
        const record = obj as { uuid?: unknown; name?: unknown; type?: unknown };
        if (typeof record.uuid !== "string" || record.uuid.length === 0) return null;
        const type = typeof record.type === "string" && record.type ? record.type : (obj.constructor?.name ?? "Object");
        const name = typeof record.name === "string" && record.name ? record.name : "";
        // weak handle → tooling can resolve the live object (select its hierarchy row)
        return { uuid: record.uuid, name, type, ref: new WeakRef(obj) };
    }

    private trackNode(node: IHierarchyNode): void {
        // Skip editor-internal objects (selection wireframes, axes/bounding-box
        // helpers, gizmos — everything hidden from the hierarchy): their
        // per-selection materials/geometries would flood the registry.
        // Helpers are added to the scene FIRST and flagged right after (the
        // node-added event fires in between) — so check the flag a microtask
        // later, when the creating code has finished.
        queueMicrotask(() => {
            for (let current: IHierarchyNode | null = node; current; current = current.parent) {
                if ((current.flags & NodeFlags.HideInHierarchy) !== 0) return;
            }
            this.trackObject(node.owner);
        });
    }

    private trackObject(owner: unknown): void {
        if (!owner || typeof owner !== "object") return;

        if (isScene(owner)) {
            const scene = owner as { environment?: unknown, background?: unknown };
            const sceneConsumer = this.usageSnapshot(owner);
            if (isTexture(scene.environment)) {
                this.resources.register(scene.environment, "texture", { type: textureTypeName(scene.environment) });
                if (sceneConsumer) this.resources.recordUsage(scene.environment, sceneConsumer);
            }
            if (isTexture(scene.background)) {
                this.resources.register(scene.background, "texture", { type: textureTypeName(scene.background) });
                if (sceneConsumer) this.resources.recordUsage(scene.background, sceneConsumer);
            }
        }

        if (!isObject3D(owner)) return;

        // Mesh, SkinnedMesh, InstancedMesh, BatchedMesh, Line, Points, Sprite — all carry
        // geometry/material; probing the properties covers them uniformly.
        const renderable = owner as unknown as { geometry?: unknown, material?: unknown };
        const consumer = this.usageSnapshot(owner);
        if (isGeometry(renderable.geometry)) {
            this.resources.register(renderable.geometry, "geometry");
            if (consumer) this.resources.recordUsage(renderable.geometry, consumer);
        }
        const material = renderable.material;
        if (Array.isArray(material)) {
            for (const mat of material) this.trackMaterial(mat, consumer);
        }
        else {
            this.trackMaterial(material, consumer);
        }
    }

    private trackMaterial(material: unknown, consumer: { uuid: string; name: string; type: string } | null = null, origin?: object): void {
        if (!isMaterial(material)) return;
        // editor-internal materials (e.g. the preview provider's ShaderMaterial
        // clones, flagged HideInHierarchy) must never surface as resources
        if (HierarchyNode.hasFlag(material, NodeFlags.HideInHierarchy)) return;
        this.resources.register(material, "material");
        if (consumer) this.resources.recordUsage(material, consumer);
        // origin = the loaded model this material was walked from (undefined on
        // the plain hierarchy sweep) — the true "came from" for the resource graph.
        if (origin) this.resources.recordOrigin(material, origin, gltfPointer(origin, material));

        // textures found on this material are consumed BY the material — the
        // property/uniform KEY is the binding slot (map, normalMap, …), passed
        // through so the resource graph can label a texture's input sockets.
        const materialConsumer = this.usageSnapshot(material);
        const trackTexture = (value: unknown, slot?: string) => {
            if (!isTexture(value)) return;
            this.resources.register(value, "texture", { type: textureTypeName(value) });
            if (materialConsumer) this.resources.recordUsage(value, materialConsumer, slot);
            if (origin) this.resources.recordOrigin(value, origin, gltfPointer(origin, value));
        };

        for (const key of Object.keys(material)) {
            trackTexture((material as unknown as Record<string, unknown>)[key], key);
        }
        const uniforms = (material as unknown as { uniforms?: Record<string, { value?: unknown }> }).uniforms;
        if (uniforms && typeof uniforms === "object") {
            for (const key of Object.keys(uniforms)) {
                trackTexture(uniforms[key]?.value, key);
            }
        }

        // TSL node materials reference textures through TextureNodes buried in the node
        // graph (colorNode, fragmentNode, …), not as direct material properties — walk
        // the graph to find them.
        if (isNodeMaterial(material)) {
            this.trackNodeGraphTextures(material, trackTexture);
        }
    }

    /**
     * Walks a node material's graph and registers every texture carried by a node
     * (a TextureNode exposes it as `.value`). Iterative with a visited set so shared
     * subgraphs and cycles are safe; the visited set is transient (released when this
     * synchronous call returns) so it never extends host-object lifetimes.
     */
    private trackNodeGraphTextures(material: NodeMaterialLike, trackTexture: (value: unknown, slot?: string) => void): void {
        try {
            const visited = new Set<NodeLike>();
            const stack: NodeLike[] = getMaterialNodeRoots(material).map((r) => r.node);
            let budget = ThreeResourceTracker.NODE_GRAPH_BUDGET;
            while (stack.length > 0 && budget-- > 0) {
                const node = stack.pop()!;
                if (visited.has(node)) continue;
                visited.add(node);
                // TextureNode / CubeTextureNode / Texture3DNode all carry the texture here;
                // the node's own name (a named uniform) is the closest thing to a slot
                trackTexture((node as unknown as { value?: unknown }).value, (node as unknown as { name?: string }).name || undefined);
                for (const { child } of getChildNodes(node)) {
                    if (!visited.has(child)) stack.push(child);
                }
            }
        }
        catch (err) {
            // graph introspection is best-effort — never let it break tracking
            this.logService.trace("ThreeResourceTracker: node-graph texture scan failed", err);
        }
    }

    /** Re-walks the hierarchy to catch material/texture swaps that fire no events. */
    private sweep(): void {
        try {
            const iterator = this._sweepIterator ?? this.hierarchy.iterateNodes()[Symbol.iterator]();
            this._sweepIterator = iterator;
            for (let visited = 0; visited < ThreeResourceTracker.SWEEP_BUDGET; visited++) {
                const next = iterator.next();
                if (next.done) {
                    this._sweepIterator = null;
                    // record model→resource edges within THIS pass (before the
                    // prune) so a loaded model's assets stay wired even when its
                    // scene isn't in the editor hierarchy — "what did I load".
                    this.trackModels();
                    this.trackMixers();
                    // full pass done — prune usage edges the pass didn't re-see.
                    // Deferred so it runs AFTER this tick's trackNode microtasks.
                    queueMicrotask(() => this.resources.completeUsagePass());
                    return;
                }
                this.trackNode(next.value);
            }
            // budget exhausted — resume from here next tick
        }
        catch (err) {
            // hierarchy mutated under a paused iterator — restart next tick
            this._sweepIterator = null;
            this.logService.trace("ThreeResourceTracker: sweep restarted", err);
        }
    }

    /**
     * Walks each loaded MODEL's subtree and records its renderable objects as
     * the model's CONTENT (model → mesh), each mesh in turn consuming the
     * geometry/material it carries (mesh → geometry/material). A model-rooted
     * resource graph therefore reads model → mesh → {geometry, material →
     * texture} — the object hierarchy is preserved, not flattened — and the
     * model's memory rollup = the total VRAM of everything it brought.
     * Budget-guarded (huge models must not stall the sweep).
     */
    private trackModels(): void {
        let budget = ThreeResourceTracker.MODEL_WALK_BUDGET;
        for (const entry of this.resources.entries("model")) {
            const result = entry.deref();
            if (!result) continue;
            for (const root of modelRoots(result)) {
                const stack: unknown[] = [root];
                while (stack.length && budget-- > 0) {
                    const obj = stack.pop();
                    if (!isObject3D(obj)) continue;
                    const renderable = obj as unknown as { geometry?: unknown; material?: unknown; children?: unknown[] };
                    const geometry = renderable.geometry;
                    const material = renderable.material;
                    const hasMat = Array.isArray(material) ? material.length > 0 : !!material;
                    // A renderable object is CONTENT of the model (model → mesh),
                    // and itself the consumer of the geometry/material it carries
                    // (mesh → geometry/material) — so the graph reads
                    // model → mesh → {geometry, material → texture}, not flattened.
                    const meshConsumer = (isGeometry(geometry) || hasMat) ? this.usageSnapshot(obj) : null;
                    if (meshConsumer) {
                        this.resources.recordUsage(result, meshConsumer);
                        if (isGeometry(geometry)) {
                            this.resources.register(geometry, "geometry");
                            this.resources.recordUsage(geometry, meshConsumer);
                            this.resources.recordOrigin(geometry, result, gltfPointer(result, geometry)); // loaded from THIS model
                        }
                        if (Array.isArray(material)) for (const m of material) this.trackMaterial(m, meshConsumer, result);
                        else if (material) this.trackMaterial(material, meshConsumer, result);
                    }
                    if (Array.isArray(renderable.children)) for (const c of renderable.children) stack.push(c);
                }
                if (budget <= 0) break;
            }
            if (budget <= 0) break;
        }
    }

    /**
     * Wires ANIMATION into the usage graph: mixer → the object it animates, and
     * clip → the mixer playing it.
     *
     * Nothing else can record these edges. A mixer is not in the object graph
     * and carries no uuid, and clips are not in the object graph either — which
     * is why "the resources this selection uses" listed a node's geometry,
     * materials and textures but never the animation driving it, even for a
     * model whose whole point is that it moves.
     *
     * Direction matches every other usage edge: the CONSUMER is what you select.
     * Selecting a mixer's root (or an ancestor of it) lights up the mixer, and
     * the mixer in turn lights up its clips as a second hop — the same shape as
     * mesh → material → texture.
     */
    private trackMixers(): void {
        for (const { entry, resource: mixer } of this.resources.resources("mixer")) {
            const root = getMixerRoot(mixer);
            const rootConsumer = root ? this.usageSnapshot(root) : null;
            if (rootConsumer) this.resources.recordUsage(mixer, rootConsumer);

            const clips = getMixerClips(mixer);
            if (clips.length === 0) continue;
            // a mixer has no uuid of its own — the registry's synthetic
            // consumer key ("mixer#12") is what the usage index is keyed by
            const mixerConsumer: ResourceUsage = {
                uuid: resourceUsageUuid(entry),
                name: entry.name,
                type: "AnimationMixer",
                ref: new WeakRef(mixer),
            };
            for (const clip of clips) {
                // the clip may come from a file we never saw load (a rig and its
                // animations are routinely separate downloads) — register first
                if (!registerAnimationClip(this.resources, clip)) continue;
                this.resources.recordUsage(clip, mixerConsumer);
            }
        }
    }
}

/**
 * A resource's glTF JSON pointer within a loaded model ("/textures/0"), read
 * from GLTFLoader's `parser.associations` (the map keyed by the live
 * material/texture/node). Undefined for non-glTF sources, or a resource the
 * loader didn't key directly (e.g. a BufferGeometry — the MESH is keyed, not it).
 */
function gltfPointer(model: object, resource: object): string | undefined {
    const assoc = (model as { parser?: { associations?: Map<object, Record<string, number>> } })
        .parser?.associations?.get(resource);
    if (!assoc) return undefined;
    // most-specific kind first (a mesh primitive is `{ meshes, primitives }`)
    for (const kind of ["textures", "materials", "meshes", "nodes", "cameras", "skins", "animations"]) {
        if (typeof assoc[kind] === "number") return `/${kind}/${assoc[kind]}`;
    }
    return undefined;
}

/** The root Object3D(s) of a loaded model: a glTF wrapper's scene(s), or the
 *  result itself when the loader returns a bare Object3D (FBX/OBJ/…). */
function modelRoots(result: object): object[] {
    if (isObject3D(result)) return [result];
    const r = result as { scene?: object; scenes?: object[] };
    const roots: object[] = [];
    if (r.scene && isObject3D(r.scene)) roots.push(r.scene);
    if (Array.isArray(r.scenes)) for (const s of r.scenes) if (isObject3D(s)) roots.push(s);
    return roots;
}
